/**
 * Constants and enums for AI For Me platform detection and tool configuration
 * Centralizes all platform-related values to avoid confusion and maintain consistency
 */

export enum ToolMaturity {
  DEVELOPMENT = "Development",
  BETA = "Beta",
  PRODUCTION = "Production",
}

export enum BasePlatform {
  AMAZON_Q = "AmazonQ",
  AMAZON_QUICK = "Amazon Quick",
  AZA = "Aza",
  CEDRIC = "Cedric",
  MENTOR = "Mentor",
  PARTYROCK_INTERNAL = "PartyRock Internal",
  PARTYROCK_EXTERNAL = "PartyRock External",
  OTHER = "Other",
}

export enum PlatformName {
  AMAZON_Q = "Amazon Q Platform",
  AMAZON_QUICK = "Amazon Quick",
  AZA = "Aza Platform",
  CEDRIC = "Cedric Platform",
  MENTOR = "Mentor Platform",
  MODEL_HOSTING = "Custom Built",
  PARTYROCK_INTERNAL = "PartyRock Internal",
  PARTYROCK_EXTERNAL = "PartyRock External",
  THIRD_PARTY = "Third-Party Tool",
}

/**
 * Enum for data classification levels
 */
export enum DataClassification {
  PUBLIC = "Public Data",
  BUSINESS_DATA = "Business Data",
  CUSTOMER_DATA = "Customer Data",
  PROHIBITED = "Prohibited Data",
  CRITICAL = "Critical Data",
  UNKNOWN = "Unknown",
}

export const TOOL_MATURITY_OPTIONS = Object.values(ToolMaturity);

export const DATA_CLASSIFICATION_OPTIONS = Object.values(DataClassification);

/**
 * Enum for security review system types (ASR/Talos detection)
 */
export enum SecurityReviewSystem {
  ASR = "ASR",
  TALOS = "Talos",
  OTHER = "Other",
}

/**
 * Enum for Yes/No questions
 */
export enum YesNo {
  YES = "Yes",
  NO = "No",
}

export const YES_NO_OPTIONS = Object.values(YesNo);

/**
 * Enum for Base Platform Metadata Taxonomy
 * Used for tools that have a recognized base platform but need additional metadata classification
 */
export enum BasePlatformMetadataTaxonomyId {
  AMAZON_Q = "atn::aiformesecmetadata/platform/amazon-q",
  AMAZON_QUICK = "atn::aiformesecmetadata/platform/amazon-quick",
  AZA = "atn::aiformesecmetadata/platform/aza",
  CEDRIC = "atn::aiformesecmetadata/platform/cedric",
  MENTOR = "atn::aiformesecmetadata/platform/mentor",
  PARTYROCK_INTERNAL = "atn::aiformesecmetadata/platform/partyrock-internal",
  PARTYROCK_EXTERNAL = "atn::aiformesecmetadata/platform/partyrock-external",
}

/**
 * URL validation patterns for ASR and Talos links
 */
export const ASR_URL_PATTERN = "^https:\\/\\/asr\\.security\\.amazon\\.dev\\/applications\\/[a-f0-9-]+\\/details\\/?$";
export const TALOS_URL_PATTERN =
  "^https:\\/\\/talos\\.security\\.aws\\.a2z\\.com\\/#\\/talos\\/engagement\\/arn:aws:talos-engagement:engagement\\/[a-f0-9-]+\\/?.*$";

/**
 * Combined pattern for HTML5 validation (ASR or Talos)
 */
export const ASR_TALOS_COMBINED_PATTERN =
  "^https:\\/\\/(asr\\.security\\.amazon\\.dev\\/applications\\/[a-f0-9-]+\\/details\\/?|talos\\.security\\.aws\\.a2z\\.com\\/#\\/talos\\/engagement\\/arn:aws:talos-engagement:engagement\\/[a-f0-9-]+\\/.*)$";

/**
 * Amazon bindle format
 */
export const BINDLE_PATTERN = "amzn1\\.bindle\\.resource\\.[a-z0-9]+";

/**
 * Interface for AI For Me form data structure
 * Used across intake form, review page, and submission
 */
export interface AiForMeFormData {
  title: string;
  description: string;
  directToolUrl: string;
  documentationUrl: string;
  selectedCategories: string[];
  selectedJobFamilies: string[];
  toolMaturity: ToolMaturity;
  dataClassification: string; // Changed from DataClassification enum to string to support dynamic values
  basePlatform: BasePlatform;
  platformName: PlatformName;
  urlAnalysis?: {
    basePlatform: BasePlatform;
    platformName: PlatformName;
    extractedData: Record<string, unknown>;
  };
  // Custom Built Tool Details (optional - only for custom built tools)
  securityReviewed?: YesNo;
  securityReviewLink?: string;
  securityReviewSystem?: SecurityReviewSystem;
  modelsUsed?: string[];
  otherModel?: string;
  storesInputPrompts?: YesNo;
  collectsDataForRetraining?: YesNo;
  username?: string;
  toolBindle?: string;
  toolCTILink?: string;
  businessJustification?: string;
  vendorName?: string;
  basePlatformMetadataTaxonomyId?: BasePlatformMetadataTaxonomyId;

  // Properties for editing
  taxonomyId?: string;
  status?: string;
  creationDate?: string;
  tags?: string[];
  /**
   * Current OCC version from the loaded tool. Required by Catalogical on
   * PUT to detect concurrent modifications — must be the stored version,
   * not a hardcoded value.
   */
  catalogicalGeneratedVersion?: number;
}
